import { isFieldConfig } from '../store/utils/field.utils';
import type { FieldCondition, FormElementConfig, FormValues } from '../types/form-builder.types';
import { collectDynamicStringValueDependencies } from './dynamic-value.utils';

type ValidatorDependencyMetadata =
    | { readonly type: 'required'; readonly condition?: boolean | FieldCondition }
    | { readonly type: 'pattern' }
    | { readonly type: 'custom'; readonly dependsOn?: readonly string[] };

const getConditionDependencies = <FormValuesType extends FormValues, FieldId extends string>(
    condition: FieldCondition<FormValuesType, FieldId>
): string[] => {
    switch (condition.type) {
        case 'when':
        case 'whenNot':
        case 'whenHasValue': {
            return [condition.field];
        }

        case 'whenAll':
        case 'whenSome': {
            return condition.conditions.flatMap((child) => getConditionDependencies(child));
        }

        case 'custom': {
            return [...condition.dependsOn];
        }

        case 'whenData': {
            return [];
        }
    }
};

const getOptionalConditionDependencies = (condition: boolean | FieldCondition | undefined): string[] => {
    if (condition === undefined || typeof condition === 'boolean') {
        return [];
    }
    return getConditionDependencies(condition);
};

const getValidatorDependencies = (validator: ValidatorDependencyMetadata): string[] => {
    switch (validator.type) {
        case 'required': {
            return validator.condition && typeof validator.condition !== 'boolean'
                ? getConditionDependencies(validator.condition)
                : [];
        }

        case 'pattern': {
            return [];
        }

        case 'custom': {
            return validator.dependsOn ? [...validator.dependsOn] : [];
        }
    }
};

export const collectDependencies = (elementConfig: FormElementConfig): string[] => {
    const dependencies = new Set<string>();

    for (const dependency of getOptionalConditionDependencies(elementConfig.disabled)) {
        dependencies.add(dependency);
    }

    for (const dependency of getOptionalConditionDependencies(elementConfig.hidden)) {
        dependencies.add(dependency);
    }

    for (const dependency of getOptionalConditionDependencies(elementConfig.showWhen)) {
        dependencies.add(dependency);
    }

    if (isFieldConfig(elementConfig)) {
        for (const dependency of collectDynamicStringValueDependencies(elementConfig.helperText)) {
            dependencies.add(dependency);
        }

        for (const validator of elementConfig.validators ?? []) {
            for (const dependency of getValidatorDependencies(validator)) {
                dependencies.add(dependency);
            }
        }
    }

    return [...dependencies];
};
